'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

export default function EmailLoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { remember: false } });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Accesso effettuato!', { description: 'Benvenuto in IA ClimbBoard.' });
      router.push('/dashboard');
      router.refresh();
    } catch (error: any) {
      toast.error(error?.message ?? 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <div>
        <label htmlFor="login-email" className="block text-sm font-semibold text-foreground mb-1.5">Email</label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="marco@example.it"
          className="input-field w-full px-4 py-2.5 text-sm"
          {...register('email', {
            required: 'Email obbligatoria',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email non valida' },
          })}
        />
        {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email.message}</p>}
      </div>

      <div>
        <label htmlFor="login-password" className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            className="input-field w-full px-4 py-2.5 pr-10 text-sm"
            {...register('password', { required: 'Password obbligatoria' })}
          />
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}>
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
          </button>
        </div>
        {errors.password && <p className="mt-1 text-xs text-red-400">{errors.password.message}</p>}
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" className="w-4 h-4 rounded border-border bg-input accent-primary" {...register('remember')} />
          <span className="text-sm text-muted-foreground">Ricordami</span>
        </label>
        <button type="button" className="text-sm text-primary hover:underline font-medium">Password dimenticata?</button>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ minHeight: '48px' }}
      >
        {loading ? (
          <>
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Accesso...
          </>
        ) : 'Accedi'}
      </button>

      {/* Demo credentials box */}
      <div className="border border-accent/30 bg-accent/5 rounded-xl p-4 mt-2">
        <p className="text-xs font-semibold text-accent mb-2 flex items-center gap-1.5">
          <Icon name="InformationCircleIcon" size={14} />
          Account demo disponibili
        </p>
        <div className="space-y-1.5">
          {[
            { role: 'Admin', email: 'admin@iaclimb.cc', password: 'IAClimb2026!' },
            { role: 'Utente', email: 'marco.bianchi@email.it', password: 'IAClimb2026!' },
          ].map((cred) => (
            <div key={`cred-${cred.role}`} className="flex items-center justify-between gap-3 bg-muted/50 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-primary badge-admin px-2 py-0.5 rounded-full">{cred.role}</span>
                <span className="text-xs text-muted-foreground font-mono-nums">{cred.email}</span>
              </div>
              <button
                type="button"
                onClick={() => { setValue('email', cred.email); setValue('password', cred.password); toast.info('Credenziali inserite'); }}
                className="text-xs text-primary hover:text-primary/80 font-semibold flex items-center gap-1 transition-colors"
              >
                <Icon name="ClipboardDocumentIcon" size={12} />
                Usa
              </button>
            </div>
          ))}
        </div>
      </div>
    </form>
  );
}